
import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error and performance monitoring
 * 
 * @param {boolean} isDevelopment - Whether the app is running in development mode
 */
export const initSentry = (isDevelopment: boolean = import.meta.env.DEV): void => {
  // Only enable debug mode in development
  const debug = isDevelopment;
  
  // Determine environment
  const environment = isDevelopment ? 'development' : 'production';

  // Initialize Sentry
  Sentry.init({
    dsn: "https://69a57546db484bbfdb3a23da062666d1@o4509113653985280.ingest.de.sentry.io/4509116518105168",
    integrations: [
      new Sentry.BrowserTracing({
        // Set sampling rate for performance monitoring (adjust as needed)
        tracePropagationTargets: ["localhost", /^https:\/\/africansdesign\./, /\.lovable\.app/],
      }),
      new Sentry.Replay(),
    ],
    // Performance monitoring sample rate
    // This sets the percentage of transactions that will be captured
    // Lower in production for better performance
    tracesSampleRate: isDevelopment ? 1.0 : 0.2,
    
    // Session replay for errors
    // Capture 100% of errors in development, less in production
    replaysSessionSampleRate: isDevelopment ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Enable debug only in development
    debug,
    
    // Set environment tag
    environment,
    
    // Don't send errors in development unless explicitly enabled
    enabled: !isDevelopment,
    
    // Ignore specific errors (e.g. network issues that are handled elsewhere)
    ignoreErrors: [
      'Network request failed',
      'Failed to fetch',
      'AbortError',
      // Add more patterns to ignore as needed
    ],
    
    // Configure beforeSend to filter sensitive information
    beforeSend(event) {
      // Don't send events from localhost unless explicitly enabled
      if (window.location.hostname === 'localhost' && !isDevelopment) {
        return null;
      }
      
      // Additional custom filtering logic can be added here
      
      return event;
    },
  });
};

/**
 * Set user context for Sentry events
 * 
 * @param {Object} user - The user object
 * @param {string} user.id - User ID
 * @param {string} [user.email] - Optional user email
 */
export const setSentryUser = (user: { id: string; email?: string } | null): void => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
  } else {
    // Clear user context when logged out
    Sentry.setUser(null);
  }
};

/**
 * Manually capture an exception with Sentry
 * 
 * @param {Error} error - The error to capture
 * @param {Object} [context] - Optional additional context
 */
export const captureException = (error: Error, context?: Record<string, any>): void => {
  Sentry.captureException(error, {
    contexts: context ? { additional: context } : undefined,
  });
};

/**
 * Add breadcrumb for tracking user journey
 * 
 * @param {string} message - Breadcrumb message
 * @param {Object} data - Additional data
 * @param {string} category - Breadcrumb category
 * @param {string} level - Severity level
 */
export const addBreadcrumb = (
  message: string, 
  data?: Record<string, any>,
  category: string = 'user-action',
  level: Sentry.SeverityLevel = 'info'
): void => {
  Sentry.addBreadcrumb({
    message,
    data,
    category,
    level,
  });
};

/**
 * Start performance transaction for measuring
 * 
 * @param {string} name - Transaction name
 * @param {string} op - Operation type
 * @returns {Sentry.Transaction} The transaction object
 */
export const startTransaction = (name: string, op: string): Sentry.Transaction => {
  return Sentry.startTransaction({ name, op });
};
